import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import { EllipsisVerticalIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import copy from 'copy-to-clipboard';

import getProductPriceLabel from '@dashboard/services/translation/getProductPriceLabel';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import PageDeleteDialog from '@dashboard/components/PageDeleteDialog';
import { getImageSrcAndSrcSet } from '@dashboard/services/getUrlFromCdn';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import { hasStock } from '@repo/lib';
import { confirm } from '@dashboard/lib/confirm';
import getProductType, { PRODUCT_TYPES } from '@dashboard/services/getProductType';
import getBillingModel, { BILLING_MODELS } from '@dashboard/services/getBillingModel';
import getActivePrices from '@dashboard/services/getActivePrices';
import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import getConversionRate from '@dashboard/services/getConversionRate';
import getCheckoutConversions from '@dashboard/services/getCheckoutConversions';
import { pagesKey, pageKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';

import { Badge } from '@dashboard/components/ui/badge';
import Statistics from '@dashboard/components/Statistic';
import DuplicatePageDialog from '@/dashboard/components/DuplicatePageDialog';
import { Button } from '@dashboard/components/ui/button';
import EditPageNameDialog from '@/dashboard/components/EditPageNameDialog';
import getRecoveredTooltipText from '@/dashboard/services/abandonment/getRecoveredTooltipText';
import getPageTheme from '@repo/checkout/lib/theming/getPageTheme';
import ViewInAdminMenuItem from '@dashboard/components/ViewInAdminMenuItem';

const propTypes = {
  page: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    description: PropTypes.string,
    currency: PropTypes.string.isRequired,
    image: PropTypes.string,
    slug: PropTypes.string.isRequired,
  }).isRequired,
};

const CheckoutListItem = ({ page, isMainView = false, isGrid = false }) => {
  const queryClient = useQueryClient();
  const { seller, sellerId, token } = useAuth();
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const { product = {} } = page;
  const [editPageNameDialogOpen, setEditPageNameDialogOpen] = useState(false);
  const history = useHistory();

  const { useDeletePage } = usePageQueries();
  const deletePage = useDeletePage();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const checkoutUrl = getCheckoutUrl(page, seller);

  async function handleDeletePage() {
    const proceed = await confirm('Archive this checkout page?', {
      description:
        'This checkout page will stop working and it will no longer be able to receive payments.',
      confirm: 'Archive',
    });

    if (!proceed) return;

    const deleted = await deletePage.mutateAsync(page._id);

    if (!deleted) {
      return window.alert('Unable to delete this checkout. Please try again or contact support.');
    }

    queryClient.invalidateQueries({ queryKey: pagesKey });
  }

  const restorePageMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/pages/${page._id}/restore`, token, {
        method: 'POST',
      }),
  });

  async function handleRestorePage() {
    const proceed = await confirm('Unarchive this checkout page?', {
      description:
        'This checkout page will start working again and it will be able to receive payments.',
      confirm: 'Unarchive',
    });

    if (!proceed) return;

    const restored = await restorePageMutation.mutateAsync();

    if (!restored) {
      return window.alert(
        'Unable to unarchive this checkout page. Please try again or contact support.',
      );
    }

    toast({
      title: 'Checkout page unarchived',
      status: 'success',
    });

    history.push(`/pages/${page._id}`);
    queryClient.invalidateQueries({ queryKey: pagesKey });
  }

  const productType = getProductType(product);

  const theme = getPageTheme(page);

  const productPrice = getProductPriceLabel(product, theme.locale);

  const billingModel = getBillingModel(product);
  const isLeadCapture = productType === PRODUCT_TYPES.LEAD_CAPTURE;
  const enabledPrices = getActivePrices(product);
  const paymentCount = page?.paymentCount ?? 0;
  const recurringSubscribers = enabledPrices
    .filter(price => price.billingType === 'recurring')
    .reduce((sum, price) => sum + (price.subscribers ?? 0), 0);

  const conversionRate = getConversionRate(
    getCheckoutConversions(product, paymentCount),
    page.visitCount,
  );

  const onNavigate = () => {
    const existingData = queryClient.getQueryData(pageKey(page._id));
    if (!existingData) {
      queryClient.setQueryData(pageKey(page._id), { ...page, seller });
    }
  };

  return (
    <div
      data-page-item
      className="relative h-[100px] w-full p-4 flex justify-start items-center transition-colors bg-white dark:bg-background hover:bg-gray-50 dark:hover:bg-muted border-b border-gray-200 dark:border-border last:border-b-0"
    >
      <RouterLink to={`/pages/${page._id}`} className="absolute inset-0 z-1" onClick={onNavigate} />

      <EditPageNameDialog
        isOpen={editPageNameDialogOpen}
        onClose={() => setEditPageNameDialogOpen(false)}
        checkout={page}
      />

      <div data-page-body className="grid xl:grid-cols-2 w-full gap-x-4">
        <div data-page-title className="xl:col-span-1 flex items-center min-w-0">
          <Thumbnail media={product.media} isGrid={isGrid} />

          <div className="truncate min-w-0 text-sm">
            <div className="flex min-w-0 items-center gap-x-2">
              <h3 className="text-sm font-semibold truncate">{page.name}</h3>

              {page.testmode === true && (
                <Badge variant="warning" className="shrink-0 tracking-wide">
                  Test mode
                </Badge>
              )}
            </div>

            <div className="flex items-baseline gap-x-2">
              {productPrice && (
                <div className="text-gray-700 dark:text-muted-foreground">{productPrice}</div>
              )}

              <div className="space-x-2">
                {!hasStock(product) && <Badge variant="black">Sold out</Badge>}

                {page.status === 'archived' && <Badge variant="info">Archived</Badge>}
              </div>
            </div>
          </div>
        </div>

        <div data-page-stats className="hidden xl:flex col-span-1 items-center justify-between">
          <Statistics>
            {isLeadCapture && (
              <>
                {page?.totalAmount > 0 ? (
                  <Statistics.Item colSpan={4}>
                    <Statistics.Text tooltip="Gross revenue from payments before refunds">
                      Revenue
                    </Statistics.Text>
                    <Statistics.Metric>
                      {getPriceLabel({
                        amount: page.totalAmount,
                        // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed
                        currency: product.currency,
                      })}
                    </Statistics.Metric>
                  </Statistics.Item>
                ) : (
                  <Statistics.Item className="stat-spacer" />
                )}

                <Statistics.Item colSpan={4}>
                  <Statistics.Text tooltip="Total number of submissions">
                    Submissions
                  </Statistics.Text>
                  <Statistics.Metric>{product.chargeCount}</Statistics.Metric>
                </Statistics.Item>
              </>
            )}

            {/* Billing columns key off the live billing model (derived from the
                product's prices), not the legacy product.type — so a mixed
                product shows both its one-time payments AND its subscribers,
                instead of being mislabelled as purely one or the other. */}
            {!isLeadCapture && billingModel === BILLING_MODELS.SUBSCRIPTION && (
              <>
                <Statistics.Item colSpan={4} className="stat-spacer">
                  <Statistics.Text></Statistics.Text>
                  <Statistics.Metric></Statistics.Metric>
                </Statistics.Item>
                <Statistics.Item colSpan={4}>
                  <Statistics.Text tooltip="Total number of focus subscribers">
                    Subscribers
                  </Statistics.Text>
                  <Statistics.Metric>{product.subscribers || 0}</Statistics.Metric>
                </Statistics.Item>
              </>
            )}

            {!isLeadCapture && billingModel === BILLING_MODELS.CHARGE && (
              <>
                <Statistics.Item colSpan={4}>
                  <Statistics.Text tooltip="Gross revenue from payments before refunds">
                    Revenue
                  </Statistics.Text>
                  <Statistics.Metric>
                    {getPriceLabel({
                      amount: page?.totalAmount ?? 0,
                      // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed
                      currency: product.currency,
                    })}
                  </Statistics.Metric>
                </Statistics.Item>
                <Statistics.Item colSpan={4}>
                  <Statistics.Text tooltip="Total number of payments">Payments</Statistics.Text>
                  <Statistics.Metric>{paymentCount}</Statistics.Metric>
                </Statistics.Item>
              </>
            )}

            {!isLeadCapture && billingModel === BILLING_MODELS.MIXED && (
              <>
                <Statistics.Item colSpan={4}>
                  <Statistics.Text tooltip="Total number of one-time payments">
                    Payments
                  </Statistics.Text>
                  <Statistics.Metric>{paymentCount}</Statistics.Metric>
                </Statistics.Item>
                <Statistics.Item colSpan={4}>
                  <Statistics.Text tooltip="Total number of subscribers">
                    Subscribers
                  </Statistics.Text>
                  <Statistics.Metric>{recurringSubscribers}</Statistics.Metric>
                </Statistics.Item>
              </>
            )}

            <Statistics.Item colSpan={3}>
              <Statistics.Text tooltip="Total number of checkout views">Views</Statistics.Text>
              <Statistics.Metric>{page.visitCount}</Statistics.Metric>
            </Statistics.Item>

            <Statistics.Item colSpan={3}>
              <Statistics.Text tooltip="Conversion Rate (conversions ÷ views × 100)">
                CVR
              </Statistics.Text>
              <Statistics.Metric>{conversionRate}%</Statistics.Metric>
            </Statistics.Item>

            <Statistics.Item colSpan={6}>
              <Statistics.Text
                tooltip={getRecoveredTooltipText(seller.checkoutAbandonment?.sendEmails)}
              >
                Recovered ({page.abandonedCartStats?.totalRecovered ?? 0}/
                {(page.abandonedCartStats?.totalAbandoned ?? 0) +
                  (page.abandonedCartStats?.totalRecovered ?? 0)}
                )
              </Statistics.Text>
              <Statistics.Metric>
                {getPriceLabel({
                  amount: page.abandonedCartStats?.totalRecoveredRevenue ?? 0,
                  // TODO(M14): reads legacy product scalars — repoint to prices[]/selected price before legacy fields are removed
                  currency: product.currency,
                })}
              </Statistics.Metric>
            </Statistics.Item>
          </Statistics>

          <div className="relative z-10 mr-4 hidden lg:block">
            <Button asChild variant="outline">
              <RouterLink to={`/pages/${page._id}/customize`} onClick={onNavigate}>
                Edit
              </RouterLink>
            </Button>
          </div>
        </div>
      </div>

      {isMainView && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-lg"
              aria-label="More options"
              className="relative z-10 mr-2"
              data-page-menu
            >
              <EllipsisVerticalIcon className="size-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={8}>
            <DropdownMenuItem onSelect={() => setEditPageNameDialogOpen(true)}>
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => {
                copy(checkoutUrl);
                toast({
                  title: 'Copied to clipboard',
                  status: 'success',
                });
              }}
            >
              Copy link
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setShowDuplicateDialog(true)}>
              Duplicate
            </DropdownMenuItem>
            {page.status === 'archived' ? (
              <DropdownMenuItem onSelect={handleRestorePage}>Unarchive</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={handleDeletePage}>Archive</DropdownMenuItem>
            )}
            <DropdownMenuItem variant="destructive" onSelect={() => setDeleteDialogOpen(true)}>
              Delete
            </DropdownMenuItem>
            <ViewInAdminMenuItem pageId={page._id} />
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <PageDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        page={page}
        label="checkout page"
      />

      <DuplicatePageDialog
        isOpen={showDuplicateDialog}
        page={page}
        onClose={() => setShowDuplicateDialog(false)}
        title="Duplicate checkout page"
      />
    </div>
  );
};

CheckoutListItem.propTypes = propTypes;

export default CheckoutListItem;

function Thumbnail({ media, isGrid }) {
  const hasMedia = media && media.length > 0;

  if (hasMedia && media[0]) {
    const image = getImageSrcAndSrcSet({
      url: media[0].url,
      // Grid cards stretch the thumbnail to the card's width.
      ...(isGrid ? { width: 400, height: 300 } : { width: 80, height: 60 }),
    });

    return (
      <img
        data-page-thumb
        className="max-w-[80px] h-[60px] flex-shrink-0 object-cover mr-4 rounded bg-gray-50 dark:bg-card"
        src={image.src}
        srcSet={image.srcSet}
      />
    );
  }

  return (
    <div
      data-page-thumb
      className="max-w-[80px] h-[60px] mr-4 rounded relative w-full flex-shrink-0 bg-gray-50 dark:bg-card border border-gray-100 dark:border-border flex items-center justify-center"
    >
      <PhotoIcon className="text-gray-200 w-6" />
    </div>
  );
}
